import { Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationLinkComponent } from '../navigation-link/navigation-link.component';
import { NavigationConfig, NavigationEvent } from '../navigation-link/interfaces/navigation.interfaces';
import { VehicleNavigationService } from '../../services/vehicle-navigation.service';
import {
  VehicleNavigationData,
  VehicleQueryParams,
  VehicleNavigationEvent,
  VehicleDisplayMode
} from './interfaces/vehicle-navigation.interfaces';

@Component({
  selector: 'app-vehicle-navigation',
  standalone: true,
  imports: [CommonModule, NavigationLinkComponent],
  templateUrl: './vehicle-navigation.component.html'
})
export class VehicleNavigationComponent {
  private readonly vehicleNavService = inject(VehicleNavigationService);

  readonly vehicle = input<VehicleNavigationData>();
  readonly displayMode = input<VehicleDisplayMode>('code');
  readonly customQueryParams = input<VehicleQueryParams>({});
  readonly openInNewTab = input<boolean>(false);
  readonly customClass = input<string>('');
  readonly showIcon = input<boolean>(true);
  readonly iconPosition = input<'left' | 'right'>('right');
  readonly icon = input<string>('heroicons_outline:rocket-launch');
  readonly showText = input<boolean>(true);
  readonly interactionMode = input<'both' | 'text-only' | 'icon-only'>('both');
  readonly customTooltipText = input<string | undefined>();

  readonly vehicleNavigate = output<VehicleNavigationEvent>();

  readonly isNavigable = computed(() =>
    this.vehicleNavService.isVehicleNavigable(this.vehicle())
  );

  readonly displayText = computed(() => {
    const v = this.vehicle();
    if (!v) return '-';

    const code = v.code || v.externalKeys?.code;
    const name = v.name;

    switch (this.displayMode()) {
      case 'code':
        return code || '-';
      case 'name':
        return name || '-';
      case 'both':
        if (code && name) return `${code} - ${name}`;
        return code || name || '-';
      default:
        return code || '-';
    }
  });

  readonly tooltipText = computed(() => {
    if (!this.isNavigable()) return '';
    return this.customTooltipText() || this.vehicleNavService.getNavigationDescription();
  });
  readonly displayCode = computed(() => {
    const d = this.vehicle();
    if (!d) return '';
    return (d as any).externalKeys?.code || (d as any).code || '';
  });

  readonly displayName = computed(() => {
    return (this.vehicle() as any)?.name || '';
  });

  readonly navigationConfig = computed<NavigationConfig<VehicleNavigationData, VehicleQueryParams>>(() => ({
    data: this.vehicle(),
    url: ['master', 'transport', 'transport-vehicle'],
    queryParams: this.customQueryParams(),
    openInNewTab: this.openInNewTab()
  }));

  readonly containerClasses = computed(() => {
    const baseClasses = 'vehicle-navigation';
    const navigableClasses = this.isNavigable() ? 'navigable' : 'non-navigable';
    return [baseClasses, navigableClasses, this.customClass()]
      .filter(cls => cls.length > 0)
      .join(' ');
  });

  handleNavigation(navigationEvent: NavigationEvent<VehicleNavigationData, VehicleQueryParams>): void {
    if (!this.isNavigable()) return;

    const vehicleNavigationEvent: VehicleNavigationEvent = {
      data: navigationEvent.config.data,
      queryParams: navigationEvent.config.queryParams || {},
      timestamp: navigationEvent.timestamp
    };

    this.vehicleNavigate.emit(vehicleNavigationEvent);

    this.vehicleNavService.navigateToVehicleDetail(
      navigationEvent.config.data,
      navigationEvent.config.queryParams,
      navigationEvent.config.openInNewTab
    );
  }
}
